import { recordAddressFor, NO_MEMBER } from './recordAddress';
import { english } from 'src/i18n/language';

const asId = (value) =>
  value === null || value === undefined ? '' : String(value).trim();

export const normaliseAttendees = (attendees, primaryId) => {
  if (!Array.isArray(attendees)) return [];
  const primary = asId(primaryId);
  const seen = new Set();
  const out = [];
  for (const value of attendees) {
    const id = asId(value);
    if (!id || id === NO_MEMBER || id === primary || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

export const isRecordForMember = (record, memberId) => {
  if (!record || memberId === null || memberId === undefined) return false;
  const id = asId(memberId);
  if (!id || id === NO_MEMBER) return false;
  if (asId(record.familyMemberId) === id) return true;
  return (
    record.noteType === 'Appointment' &&
    Array.isArray(record.attendees) &&
    record.attendees.some((a) => asId(a) === id)
  );
};

// Each row's label is English, translated where it is shown; a person with no
// name here reads in the caller's language (`t`, English when omitted).
export const appointmentPeopleRows = (
  record,
  viewerId,
  personLabel,
  t = english
) => {
  if (!record || record.noteType !== 'Appointment') return [];
  const name = (id) =>
    (personLabel && personLabel(id)) || t('Person not in this family');
  const attendees = normaliseAttendees(record.attendees, record.familyMemberId);
  const rows = [];
  if (asId(viewerId) && asId(viewerId) !== asId(record.familyMemberId)) {
    rows.push({ label: 'Primary person', value: name(record.familyMemberId) });
  }
  if (attendees.length > 0) {
    rows.push({ label: 'Attendees', value: attendees.map(name).join(', ') });
  }
  return rows;
};

const fullNameOf = (member) =>
  [member?.firstName, member?.lastName].filter(Boolean).join(' ').trim();

const upnOf = (member) => {
  if (member?.proBandId) return '1';
  const upn = member?.upn;
  return upn === null || upn === undefined || upn === '' ? '' : String(upn);
};

const labelFor = (name, upn) => {
  if (name && upn) return `UPN ${upn} - ${name}`;
  if (name) return name;
  return upn ? `UPN ${upn}` : 'Unnamed person';
};

export const attendeeOptions = (members, primaryId, nameSources = []) => {
  const primary = asId(primaryId);
  const known = new Map();
  for (const source of Array.isArray(nameSources) ? nameSources : []) {
    const id = asId(recordAddressFor(source));
    if (id && id !== NO_MEMBER && !known.has(id)) known.set(id, source);
  }
  const seen = new Set();
  const out = [];
  for (const member of Array.isArray(members) ? members : []) {
    const id = asId(recordAddressFor(member));
    if (!id || id === NO_MEMBER || id === primary || seen.has(id)) continue;
    seen.add(id);
    const fallback = known.get(id);
    const name = fullNameOf(member) || fullNameOf(fallback);
    const upn = upnOf(member) || upnOf(fallback);
    const dob = member?.dob || fallback?.dob || '';
    out.push({ value: id, label: labelFor(name, upn), name, upn, dob });
  }

  return out.sort((a, b) => {
    if (!a.upn || !b.upn) return a.upn ? -1 : b.upn ? 1 : 0;
    return a.upn.localeCompare(b.upn, undefined, { numeric: true });
  });
};
